import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useCountdown } from '@/composables/useCountdown';

const URGENT_THRESHOLD_MS = 10 * 60 * 1000;
const COUNTDOWN_THRESHOLD_MS = 24 * 60 * 60 * 1000;

/**
 * LIVE/OPENS-IN/CLOSED status for the currently-selected contract, driving the title-area
 * status badge. Opening and closing countdowns are only presented during their final 24 hours.
 * Before that opening window the badge is hidden; while live and more than 24 hours from close,
 * only the LIVE indicator is shown. Computes its own 10-minute urgency threshold locally rather
 * than reusing `useCountdown`'s 5-minute `isUrgent` — that threshold is shared by unrelated
 * existing UI and shouldn't change for those consumers.
 */
export function useLiveStatus(
  lastTradeTime: MaybeRefOrGetter<number | undefined>,
  startTime?: MaybeRefOrGetter<number | undefined>,
) {
  const { label, remainingMs, isExpired } = useCountdown(() => toValue(lastTradeTime) ?? 0);
  const { label: opensInLabel, remainingMs: remainingUntilStartMs } = useCountdown(() => toValue(startTime) ?? 0);

  // Ticks alongside `remainingUntilStartMs` (both driven by useCountdown's internal timer),
  // unlike checking `toValue(startTime)` directly which would only ever be evaluated once.
  const isNotYetOpen = computed(() => (toValue(startTime) ?? 0) > 0 && remainingUntilStartMs.value > 0);
  const isOpeningSoon = computed(() => isNotYetOpen.value && remainingUntilStartMs.value <= COUNTDOWN_THRESHOLD_MS);
  const isClosingSoon = computed(() => remainingMs.value > 0 && remainingMs.value <= COUNTDOWN_THRESHOLD_MS);
  const isUrgent = computed(() => remainingMs.value > 0 && remainingMs.value < URGENT_THRESHOLD_MS);
  const isVisible = computed(() => !isNotYetOpen.value || isOpeningSoon.value);

  return {
    countdownLabel: label,
    opensInLabel,
    remainingUntilStartMs,
    isUrgent,
    isClosed: isExpired,
    isNotYetOpen,
    isOpeningSoon,
    isClosingSoon,
    isVisible,
  };
}
